import { AvatarDataSource, AvatarLevel, ClientAvatarType, Prisma } from "@prisma/client";

export type ProjectOfferData = {
  id: number;
  uuid: string;
  name: string;
  type: string | null;
  problem: string | null;
  promise: string | null;
  price: string | null;
  proof: string[];
  keyAdvantages: string[];
  _count?: {
    clientAvatars: number;
  };
};

export const PROJECT_OFFER_SELECT = {
  id: true,
  uuid: true,
  name: true,
  type: true,
  problem: true,
  promise: true,
  price: true,
  proof: true,
  keyAdvantages: true,
  _count: {
    select: {
      clientAvatars: true,
    },
  },
} satisfies Prisma.ProjectOfferSelect;

export enum AvatarField {
  NAME = "name",
  ROLE = "role",
  GENDER = "gender",
  AGE_RANGE = "ageRange",
  LOCATION_RANGE = "locationRange",
  EDUCATION_LEVEL = "educationLevel",
  INCOME_RANGE = "incomeRange",
  COMPANY_TYPE = "companyType",
  COMPANY_SIZE = "companySize",
  MARKET = "market",
  SNAPSHOT_SUMMARY = "snapshotSummary",
  PRIORITY = "priority",
  DESIRES = "desires",
  FRUSTRATIONS = "frustrations",
  DREAMS = "dreams",
  FEARS = "fears",
  COST_TIME = "costTime",
  COST_ENERGY = "costEnergy",
  COST_OPPORTUNITY = "costOpportunity",
  CURRENT_SITUATION = "currentSituation",
  DESIRED_SITUATION = "desiredSituation",
  DAILY_ROUTINE = "dailyRoutine",
  MINDSET = "mindset",
  BLOCKERS = "blockers",
  FALSE_BELIEF = "falseBelief",
  PURCHASE_OBJECTIONS = "purchaseObjections",
  OBJECTION_HANDLER = "objectionHandler",
  PRIVATE_QUOTES = "privateQuotes",
  PUBLIC_QUOTES = "publicQuotes",
  DOMINANT_KEYWORDS = "dominantKeywords",
  EMOTIONAL_LANGUAGE = "emotionalLanguage",
  COPY_ADVICE = "copyAdvice",
  URGENCY_DESCRIPTION = "urgencyDescription",
  PURCHASE_POTENTIAL_DESCRIPTION = "purchasePotentialDescription",
  IDEAL_CLIENT_CRITERIA = "idealClientCriteria",
  NON_IDEAL_CLIENT_CRITERIA = "nonIdealClientCriteria",
  PURCHASE_TRIGGERS = "purchaseTriggers",
  WHERE_TO_FIND = "whereToFind",
  VOICE_OF_TRUST = "voiceOfTrust",
  APPROACH_ANGLE = "approachAngle",
}

export type UserClientAvatar = {
  id: number;
  uuid: string;
  offerId: number;
  type: ClientAvatarType;

  // Identity
  avatarUrl: string;
  name: string;
  role: string;
  gender: string;
  ageRange: string[];
  locationRange: string[];
  educationLevel: string | null;
  incomeRange: string | null;
  companyType: string | null;
  companySize: string | null;
  market: string | null;

  // Overview
  snapshotSummary: string | null;
  priority: string | null;
  dataSources: AvatarDataSource[];

  // Scores
  scoreFit: number | null;
  scoreConfidence: number | null;
  scorePain: number | null;

  // Pains & Desires
  desires: string[];
  frustrations: string[];
  dreams: string[];
  fears: string[];
  costTime: string | null;
  costEnergy: string | null;
  costOpportunity: string | null;

  // Situation
  currentSituation: string[];
  desiredSituation: string[];
  dailyRoutine: DailyRoutineItem[] | null;
  mindset: string | null;
  blockers: string[];

  // Objections
  falseBelief: string[];
  purchaseObjections: string[];
  objectionHandler: string[];

  // Verbatims
  privateQuotes: string[];
  publicQuotes: string[];
  dominantKeywords: string[];
  emotionalLanguage: string[];
  copyAdvice: string | null;

  // Fit & Qualification
  fitSummary: string | null;
  urgencyLevel: AvatarLevel | null;
  urgencyDescription: string | null;
  purchasePotential: AvatarLevel | null;
  purchasePotentialDescription: string | null;
  idealClientCriteria: string[];
  nonIdealClientCriteria: string[];
  purchaseTriggers: string[];
  whereToFind: string[];
  voiceOfTrust: string | null;
  approachAngle: string | null;
};

export type DailyRoutineItem = {
  time: string;
  text: string;
};

export const LIGHT_CLIENT_AVATAR_SELECT = {
  id: true,
  uuid: true,
  offerId: true,
  type: true,
  avatarUrl: true,
  name: true,
  role: true,
  gender: true,
  ageRange: true,
  snapshotSummary: true,
} satisfies Prisma.ClientAvatarSelect;

export const AVATAR_ABOUT_SELECT = {
  id: true,
  locationRange: true,
  educationLevel: true,
  incomeRange: true,
  companyType: true,
  companySize: true,
  market: true,
  scoreFit: true,
  scoreConfidence: true,
  scorePain: true,
  priority: true,
  dataSources: true,
} satisfies Prisma.ClientAvatarSelect;

export const AVATAR_PAIN_SELECT = {
  id: true,
  desires: true,
  frustrations: true,
  dreams: true,
  fears: true,
  costTime: true,
  costEnergy: true,
  costOpportunity: true,
} satisfies Prisma.ClientAvatarSelect;

export const AVATAR_SITUATION_SELECT = {
  id: true,
  currentSituation: true,
  desiredSituation: true,
  dailyRoutine: true,
  mindset: true,
  blockers: true,
} satisfies Prisma.ClientAvatarSelect;

export const AVATAR_OBJECTIONS_SELECT = {
  id: true,
  falseBelief: true,
  purchaseObjections: true,
  objectionHandler: true,
} satisfies Prisma.ClientAvatarSelect;

export const AVATAR_VERBATIMS_SELECT = {
  id: true,
  privateQuotes: true,
  publicQuotes: true,
  dominantKeywords: true,
  emotionalLanguage: true,
  copyAdvice: true,
} satisfies Prisma.ClientAvatarSelect;

export const AVATAR_FIT_SELECT = {
  id: true,
  scoreFit: true,
  scoreConfidence: true,
  scorePain: true,
  fitSummary: true,
  urgencyLevel: true,
  urgencyDescription: true,
  purchasePotential: true,
  purchasePotentialDescription: true,
  idealClientCriteria: true,
  nonIdealClientCriteria: true,
  purchaseTriggers: true,
  whereToFind: true,
  voiceOfTrust: true,
  approachAngle: true,
} satisfies Prisma.ClientAvatarSelect;

export const FULL_CLIENT_AVATAR_SELECT = {
  ...LIGHT_CLIENT_AVATAR_SELECT,
  ...AVATAR_ABOUT_SELECT,
  ...AVATAR_PAIN_SELECT,
  ...AVATAR_SITUATION_SELECT,
  ...AVATAR_OBJECTIONS_SELECT,
  ...AVATAR_VERBATIMS_SELECT,
  ...AVATAR_FIT_SELECT,
} satisfies Prisma.ClientAvatarSelect;
